import Link from 'next/link';
import { CardProps } from './types';
import DirectusImage from '../image/DirectusImage';
import TitleH2 from '../common/TitleH2';
import ButtonLink from '../link/ButtonLink';
import WhiteBallIcon from '../../../../public/svg/ball_white.svg';
import Image from 'next/image';

const CardPush = ({
  background,
  backgroundEllipse = 'md:bg-card-small-push-no-image-right-ellipse',
  button,
  callToActionUrl: { url, external, slug },
  columnCount,
  image,
  imageAlt,
  imageSizes,
  stylesImage,
  title,
  intro,
  imageBlur,
  animated,
}: CardProps) => {
  const sizes = imageSizes || '100vw';
  const link = external ? (url || slug) : (slug || url);
  const target = external ? '_blank' : '_self';
  let transparentBg = false;
  let border = true;

  if (button) {
    transparentBg =
      button && ['secondary', 'transparent_with_ball'].includes(button.buttonStyle ?? '');
    border = !button.buttonStyle || button.buttonStyle === 'primary';
  }

  const gradientImage = image
    ? 'from-astro-gradient-start to-astro-gradient-end bg-gradient-to-t'
    : '';

  let cardBackground = image ? '' : 'bg-gray-100';
  if (!image && background === 'blue') {
    cardBackground = 'bg-indigo-950';
  }
  if (!image && background === 'gradient') {
    cardBackground = 'bg-gradient-to-r from-blue-900 via-blue-900 to-red-600';
  }

  let textColorType = (image ? 'default' : 'black') as 'default' | 'black';
  if (background === 'blue') {
    textColorType = 'default';
  }

  if (background === 'gradient') {
    textColorType = 'default';
  }

  let extraEllipseColumnOverrides = '';

  if (columnCount === 2) {
    extraEllipseColumnOverrides =
      'md:bg-card-small-push-no-image-ellipse-two-columns md:bg-card-small-push-no-image-right-ellipse-two-columns';
  }
  if (columnCount === 3) {
    extraEllipseColumnOverrides =
      'md:bg-card-small-push-no-image-ellipse-three-columns md:bg-card-small-push-no-image-right-ellipse-three-columns';
  }
  const cardTextAlignment = image ? 'justify-end' : 'justify-center items-center';
  const ellipseBackground = image
    ? ''
    : `bg-card-small-push-no-image-ellipse-mobile md:bg-card-small-push-no-image-ellipse bg-card-small-push-no-image-right-ellipse-mobile ${backgroundEllipse} ${extraEllipseColumnOverrides}`;

  let buttonFontColor = 'text-black';

  if (!image) {
    if (button && button.buttonStyle === 'secondary') {
      buttonFontColor = 'text-black';

      if (background === 'gradient' || background === 'blue') {
        buttonFontColor = 'text-white';
      }
    }
  } else {
    if (button && ['secondary', 'transparent_with_ball'].includes(button.buttonStyle ?? '')) {
      buttonFontColor = 'text-white';
    }
  }

  return (
    <div
      data-test-id="card-push"
      className={`${cardBackground} ${ellipseBackground} md:py-auto card relative flex w-full flex-col rounded-md ${cardTextAlignment} ${stylesImage ? stylesImage : 'h-full'} ${animated && 'md:duration-700 md:hover:translate-y-[-1em]'}`}
    >
      {!image && <div className="md:bg-card-small-push-no-image" />}
      {image && (
        <div
          className={`${gradientImage} absolute bottom-0 z-10 h-full w-full rounded-md md:transition-transform`}
        />
      )}
      <Link
        href={link || ''}
        target={target}
        className={`group ${image ? 'h-full' : 'h-full w-full'} relative overflow-hidden rounded-md`}
      >
        {image && (
          <DirectusImage
            src={image || ''}
            alt={imageAlt || ''}
            blur={imageBlur}
            dimension={{ sizes }}
            styles={`bg-black opacity-90 ${animated && 'group-hover:scale-110 transition-transform duration-500 ease-in-out'}`}
          />
        )}

        <div
          className={`relative z-10 py-[40px] md:px-10 md:py-[50px] ${image ? `flex h-full min-h-60 flex-col items-start justify-end` : `flex h-full flex-col justify-center items-center`}`}
        >
          {intro && (
            <div className={`mx-5 flex h-[15px] ${animated ? 'items-end' : 'items-center'} justify-center gap-2 md:mx-7 lg:mx-0 `}>
              {image && <Image src={WhiteBallIcon} width={13} height={13} alt={intro} />}
              <span className={`text-[11px] text-xs uppercase leading-3 ${image ? 'text-white' : 'mb-[30px] text-gray-400'}`}>{intro}</span>
            </div>
          )}
          {!image && (
            <TitleH2
              color={textColorType}
              customFont={true}
              heading={false}
              size="default"
              styles="w-full mx-5 lg:mx-0 text-center"
              titleBold={false}
              titleUpperCase={true}
              titleTracking={`leading-[33px] lg:leading-10 lg:tracking-[1.35px]`}
            >
              {title}
            </TitleH2>
          )}
          {image && (
            <TitleH2
              color={textColorType}
              customFont={false}
              heading={false}
              size="small"
              styles="w-[85%] md:w-full mx-5 lg:mx-0 md:mx-7 lg:mt-[30px] mt-[20px]"
              titleBold={false}
              titleUpperCase={false}
              titleTracking="leading-0 lg:leading-8 lg:tracking-[1.35px]"
            >
              {title}
            </TitleH2>
          )}

          {button && button.buttonLabel && (
            <ButtonLink
              transparentBg={transparentBg}
              name={button.buttonLabel || ''}
              styles={`${image ? 'mx-5 lg:mx-0' : ''} ${buttonFontColor} ${transparentBg ? 'pt-[30px] pb-0' : 'mt-4'}`}
              border={border}
              buttonStyle={button.buttonStyle}
            />
          )}
        </div>
      </Link>
    </div>
  );
};

export default CardPush;
